import { createContext, useReducer, useMemo, useCallback, useContext, useRef } from "react";
import { useSearchParamsSync } from "./useSearchParamsSync";
import datatableContextSlice, {
  DatatableContextState,
  DatatableFilter,
  FilterValue,
  initialState,
  removeFilter,
  setFilters,
  updateFilterValues,
} from "./datatableSlice";
import { FilterType } from "../types";
import { StoredFilterConfig } from "./datatableSlice";

/**
 * Filter configuration for programmatic filter registration.
 * Use this when registering filters via registerFilters() instead of table headers.
 */
export interface FilterConfig extends StoredFilterConfig {
  /** Filter label (required) */
  label: string;
  /** Default value for the filter */
  defaultValue?: string | string[] | number | Date;
  /** Whether the filter value is an array (for params that use getAll()) */
  isArray?: boolean;
  /** Whether this filter should appear in the "More Filters" sheet. Defaults to true if searchKey is provided. */
  showInFiltersSheet?: boolean;
}

interface ContextProps {
  state: DatatableContextState;
  dispatch: React.Dispatch<Parameters<typeof datatableContextSlice.reducer>[1]>;
  // Helper functions
  getFilterValue: <T = string | string[] | number | Date>(key: string) => T | undefined;
  // register filters from header or from page (date range)
  registerFilters: (filters: Record<string, FilterConfig>) => void;
  // Flag indicating if initial sync from URL is complete
  isInitialized: boolean;
}

export const DatatableContext = createContext<ContextProps>({
  state: initialState,
  dispatch: () => null,
  getFilterValue: () => undefined,
  registerFilters: () => {},
  isInitialized: false,
});

interface DatatableContextProviderProps {
  children: React.ReactNode;
}

export function DatatableContextProvider({ children }: DatatableContextProviderProps) {
  const [state, dispatch] = useReducer(
    datatableContextSlice.reducer,
    datatableContextSlice.getInitialState()
  );

  // Track registered filter keys
  const registeredKeysRef = useRef<Set<string>>(new Set());

  const currentFilterKeys = Object.keys(state.filters);

  const onFiltersUpdate = useCallback(
    (filtersToUpdate: Array<{ columnId: string; value: string[] }>, removedKeys: string[]) => {
      // Batch update filter values if we have any
      if (filtersToUpdate.length > 0) {
        dispatch(updateFilterValues(filtersToUpdate));
      }

      // Remove filters for keys that are no longer registered
      removedKeys.forEach(key => {
        dispatch(removeFilter({ columnId: key }));
      });
    },
    []
  );

  // Handle search params synchronization and get initialization status
  const isInitialized = useSearchParamsSync({
    currentFilters: currentFilterKeys,
    updateFilters: onFiltersUpdate,
  });

  // Function to register new filter keys
  const registerFilterKey = useCallback((searchKey: string) => {
    registeredKeysRef.current.add(searchKey);
  }, []);

  // Helper functions
  const getFilterValue = useCallback(
    <T = string | string[] | number | Date,>(key: string): T | undefined => {
      return state.filters[key]?.filterValue.value as T | undefined;
    },
    [state.filters]
  );

  // Unified function to register any filters
  const registerFilters = useCallback(
    (filters: Record<string, FilterConfig>) => {
      const filterPayloads = Object.entries(filters).map(([key, config]) => {
        // Register this key for search params sync
        registerFilterKey(key);

        const { showInFiltersSheet, ...filterBaseProps } = config;

        return {
          columnId: key,
          filterData: {
            filterValue: {
              type: config.filterType || FilterType.TEXT, // Use provided type or default to TEXT
              value: "",
            },
            label: config.label,
            // Include the filter configuration for rendering in the sheet modal
            // Only include if showInFiltersSheet is not explicitly false
            filterConfig: showInFiltersSheet !== false ? filterBaseProps : undefined,
          },
        };
      });

      // Dispatch all filters at once
      if (filterPayloads.length > 0) {
        dispatch(setFilters(filterPayloads));
      }
    },
    [dispatch, registerFilterKey]
  );

  const values = useMemo(
    () => ({
      state,
      dispatch,
      getFilterValue,
      registerFilters,
      isInitialized,
    }),
    [state, dispatch, getFilterValue, registerFilters, isInitialized]
  );

  return <DatatableContext.Provider value={values}>{children}</DatatableContext.Provider>;
}

// Custom hook for using the context
export const useDatatableContext = () => {
  return useContext(DatatableContext);
};

// Export types for use in components
export type { DatatableFilter, FilterValue };

export default DatatableContext;
